/**
 * Compact year range slider for filtering data
 */

import React, { useRef, useCallback } from 'react'

interface YearRangeSliderProps {
  value: [number, number]
  min: number
  max: number
  onChange: (value: [number, number]) => void
  compact?: boolean
}

export const YearRangeSlider: React.FC<YearRangeSliderProps> = ({
  value,
  min,
  max,
  onChange,
  compact = false,
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef<'start' | 'end' | null>(null)

  // Calculate positions for the visual range indicator
  const startPercent = ((value[0] - min) / (max - min)) * 100
  const endPercent = ((value[1] - min) / (max - min)) * 100

  const getYearFromPosition = useCallback(
    (clientX: number): number => {
      if (!trackRef.current) return min
      const rect = trackRef.current.getBoundingClientRect()
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      const year = Math.round(min + percent * (max - min))
      return Math.max(min, Math.min(max, year))
    },
    [min, max]
  )

  const handleMouseDown = useCallback(
    (thumb: 'start' | 'end') => (e: React.MouseEvent) => {
      e.preventDefault()
      draggingRef.current = thumb

      const handleMouseMove = (e: MouseEvent) => {
        const year = getYearFromPosition(e.clientX)
        if (draggingRef.current === 'start') {
          if (year <= value[1]) {
            onChange([year, value[1]])
          }
        } else if (draggingRef.current === 'end') {
          if (year >= value[0]) {
            onChange([value[0], year])
          }
        }
      }

      const handleMouseUp = () => {
        draggingRef.current = null
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [value, onChange, getYearFromPosition]
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      const year = getYearFromPosition(e.clientX)
      // Move the closest thumb
      const distToStart = Math.abs(year - value[0])
      const distToEnd = Math.abs(year - value[1])

      if (distToStart <= distToEnd) {
        if (year <= value[1]) {
          onChange([year, value[1]])
        }
      } else {
        if (year >= value[0]) {
          onChange([value[0], year])
        }
      }
    },
    [value, onChange, getYearFromPosition]
  )

  // Generate year marks for the track
  const years = []
  for (let y = min; y <= max; y++) {
    years.push(y)
  }

  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <span className="text-xs text-secondary whitespace-nowrap">
          {value[0]} - {value[1]}
        </span>
        <div
          ref={trackRef}
          className="relative flex-1 h-1.5 cursor-pointer"
          onClick={handleTrackClick}
        >
          {/* Track background */}
          <div className="absolute inset-0 bg-tertiary rounded-full" />

          {/* Active range */}
          <div
            className="absolute h-full bg-accent rounded-full"
            style={{
              left: `${startPercent}%`,
              width: `${endPercent - startPercent}%`,
            }}
          />

          {/* Start thumb */}
          <div
            className="absolute w-3 h-3 -mt-[3px] bg-primary border-2 border-accent rounded-full shadow-sm cursor-grab active:cursor-grabbing transform -translate-x-1/2 hover:scale-110 transition-transform"
            style={{ left: `${startPercent}%` }}
            onMouseDown={handleMouseDown('start')}
          />

          {/* End thumb */}
          <div
            className="absolute w-3 h-3 -mt-[3px] bg-primary border-2 border-accent rounded-full shadow-sm cursor-grab active:cursor-grabbing transform -translate-x-1/2 hover:scale-110 transition-transform"
            style={{ left: `${endPercent}%` }}
            onMouseDown={handleMouseDown('end')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-secondary">
        <span>Year Range</span>
        <span className="font-medium text-primary">
          {value[0]} - {value[1]}
        </span>
      </div>

      <div
        ref={trackRef}
        className="relative h-2 cursor-pointer"
        onClick={handleTrackClick}
      >
        {/* Track background */}
        <div className="absolute inset-0 bg-tertiary rounded-full" />

        {/* Active range */}
        <div
          className="absolute h-full bg-accent rounded-full"
          style={{
            left: `${startPercent}%`,
            width: `${endPercent - startPercent}%`,
          }}
        />

        {/* Start thumb */}
        <div
          className="absolute w-4 h-4 -mt-1 bg-primary border-2 border-accent rounded-full shadow-sm cursor-grab active:cursor-grabbing transform -translate-x-1/2 hover:scale-110 transition-transform"
          style={{ left: `${startPercent}%` }}
          onMouseDown={handleMouseDown('start')}
        />

        {/* End thumb */}
        <div
          className="absolute w-4 h-4 -mt-1 bg-primary border-2 border-accent rounded-full shadow-sm cursor-grab active:cursor-grabbing transform -translate-x-1/2 hover:scale-110 transition-transform"
          style={{ left: `${endPercent}%` }}
          onMouseDown={handleMouseDown('end')}
        />
      </div>

      {/* Year marks */}
      <div className="flex justify-between text-xs text-secondary">
        {years.map((y) => (
          <span key={y}>{y}</span>
        ))}
      </div>
    </div>
  )
}

export default YearRangeSlider
